import { Injectable } from "@angular/core";
import { BehaviorSubject, lastValueFrom, Observable } from "rxjs";
import {
  UserGlobalSettingsWithRelations,
  UserInventoryPreferenceWithRelations,
} from "src/app/api/models";
import {
  UserGlobalSettingsControllerService,
  UserInventoryPreferenceControllerService,
} from "src/app/api/services";
import { merge } from "lodash";

@Injectable({
  providedIn: "root",
})
export class SettingsService {
  private globalSettings: BehaviorSubject<
    UserGlobalSettingsWithRelations | undefined
  > = new BehaviorSubject<UserGlobalSettingsWithRelations | undefined>(
    undefined
  );

  globalSettings$: Observable<UserGlobalSettingsWithRelations | undefined> =
    this.globalSettings;

  private inventoryPreference: BehaviorSubject<
    UserInventoryPreferenceWithRelations | undefined
  > = new BehaviorSubject<UserInventoryPreferenceWithRelations | undefined>(
    undefined
  );

  inventoryPreference$: Observable<
    UserInventoryPreferenceWithRelations | undefined
  > = this.inventoryPreference;

  constructor(
    private userGlobalSettingsService: UserGlobalSettingsControllerService,
    private userInventoryPreferenceService: UserInventoryPreferenceControllerService
  ) {}

  /**
   * Loads the global settings from the server and updates the globalSettings BehaviorSubject.
   *
   * @returns {Promise<void>} - A promise that resolves when the settings are loaded.
   */
  async loadGlobalSettings(): Promise<void> {
    const settings = await lastValueFrom(this.userGlobalSettingsService.find());
    this.globalSettings.next(settings);
  }

  /**
   * Saves the current global settings to the server.
   *
   * @returns {Promise<void>} - A promise that resolves when the settings are saved.
   */
  private async saveGlobalSettings(): Promise<void> {
    const settings = this.globalSettings.value;
    if (settings) {
      await lastValueFrom(
        this.userGlobalSettingsService.updateById({ body: settings })
      );
    }
  }

  /**
   * Gets the current global settings value.
   *
   * @returns {UserGlobalSettingsWithRelations | undefined} - The current global settings value.
   */
  get globalSettingsValue(): UserGlobalSettingsWithRelations | undefined {
    const value = this.globalSettings.value;
    return value;
  }

  /**
   * Updates the global settings with the provided partial update.
   *
   * @param {Partial<UserGlobalSettingsWithRelations>} update - The partial update for the global settings.
   */
  updateGlobalSettings(update: Partial<UserGlobalSettingsWithRelations>): void {
    const currentSettings = this.globalSettings.value;
    const newSettings = merge({}, currentSettings, update);
    this.globalSettings.next(newSettings);
    try {
      this.saveGlobalSettings();
    } catch (error) {
      console.error("Failed to save settings", error);
    }
  }

  /**
   * Loads the inventory preferences for the specified inventory ID.
   *
   * @param {string} inventoryId - The ID of the inventory to load preferences for.
   * @returns {Promise<void>} - A promise that resolves when the preferences are loaded.
   */
  async loadInventoryPreferences(inventoryId: string): Promise<void> {
    const preference = await lastValueFrom(
      this.userInventoryPreferenceService.find({ inventoryId })
    );
    this.inventoryPreference.next(preference);
  }

  /**
   * Saves the current inventory preferences to the server.
   *
   * @returns {Promise<void>} - A promise that resolves when the preferences are saved.
   */
  private async saveInventoryPreferences(): Promise<void> {
    const preference = this.inventoryPreference.value;
    if (preference) {
      await lastValueFrom(
        this.userInventoryPreferenceService.updateById({
          inventoryId: preference.inventoryId,
          body: preference,
        })
      );
    }
  }

  /**
   * Gets the current inventory preference value.
   *
   * @returns {UserInventoryPreferenceWithRelations | undefined} - The current inventory preference value.
   */
  get inventoryPreferenceValue():
    | UserInventoryPreferenceWithRelations
    | undefined {
    const value = this.inventoryPreference.value;
    return value;
  }

  /**
   * Updates the inventory preference with the provided partial update.
   *
   * @param {Partial<UserInventoryPreferenceWithRelations>} update - The partial update for the inventory preference.
   */
  async updateInventoryPreference(
    update: Partial<UserInventoryPreferenceWithRelations>
  ): Promise<void> {
    const currentPreference = this.inventoryPreference.value;
    // Remove the filter property if it is present in the update
    // NOTE: merge() not working as expected, so manually removing the filter property
    if (update.itemsList?.["filter"]) {
      delete currentPreference.itemsList["filter"];
    }
    if (update["galleryList"]?.["filter"]) {
      if (!currentPreference["galleryList"]) currentPreference["galleryList"] = {};
      delete currentPreference["galleryList"]["filter"];
    }
    const newPreference = merge({}, currentPreference, update);
    this.inventoryPreference.next(newPreference);

    await this.saveInventoryPreferences();
  }
}
